import { Lock } from "lucide-react";
import { useState } from "react";
import { authClient } from "../../lib/auth-client";
import { Aviso, Boton, Campo } from "../ui/primitivos";

/**
 * Pantalla de reingreso tras el bloqueo por inactividad (ADR 0034).
 *
 * Porteada de `SessionExpiredModal.tsx`, con su regla de 3 intentos: al tercero
 * se cierra la sesión de verdad y hay que volver por el login. Sin eso, tapar la
 * pantalla no le costaría nada a quien se sienta a probar contraseñas — y el
 * plazo largo juega a su favor, porque nadie va a volver al escritorio pronto.
 *
 * El email se muestra y **no se edita**: esto es volver a entrar como la misma
 * persona, no un login. Cambiarlo acá sería un login encubierto sobre una
 * pantalla que ya tiene datos de otra sesión a la vista.
 */

const MAXIMO_INTENTOS = 3;

export function BloqueoPorInactividad({
  email,
  minutos,
  alDesbloquear,
  alSalir,
}: {
  email: string;
  minutos: number;
  alDesbloquear: () => void;
  alSalir: () => Promise<void>;
}) {
  const [contrasena, setContrasena] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [trabajando, setTrabajando] = useState(false);
  const [intentos, setIntentos] = useState(0);

  async function reingresar() {
    setTrabajando(true);
    setError(null);
    try {
      const r = await authClient.signIn.email({ email, password: contrasena });
      if (r.error) throw new Error(r.error.message ?? "No se pudo verificar");
      setContrasena("");
      setIntentos(0);
      alDesbloquear();
    } catch {
      const usados = intentos + 1;
      setIntentos(usados);
      setContrasena("");
      if (usados >= MAXIMO_INTENTOS) {
        setError("Demasiados intentos. Se va a cerrar la sesión.");
        await alSalir();
        return;
      }
      const quedan = MAXIMO_INTENTOS - usados;
      setError(
        `Contraseña incorrecta. ${quedan === 1 ? "Queda 1 intento" : `Quedan ${quedan} intentos`}.`,
      );
    } finally {
      setTrabajando(false);
    }
  }

  return (
    // `fixed inset-0` sin forma de cerrar: no hay X, ni Escape, ni clic afuera.
    // Un modal descartable no bloquearía nada.
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/60 backdrop-blur-sm">
      <div className="mx-4 w-full max-w-md rounded-xl border border-neutral-200 bg-white shadow-xl">
        <div className="flex items-center gap-3 rounded-t-xl border-neutral-200 border-b bg-amber-50 px-6 py-4">
          <div className="rounded-full bg-amber-100 p-2">
            <Lock aria-hidden="true" className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <h2 className="font-semibold text-neutral-900">Pantalla bloqueada</h2>
            <p className="text-neutral-600 text-sm">
              Pasaron {minutos} minutos sin actividad. Ingresá tu contraseña para seguir.
            </p>
          </div>
        </div>

        <form
          className="space-y-4 p-6"
          onSubmit={(e) => {
            e.preventDefault();
            void reingresar();
          }}
        >
          {error ? <Aviso>{error}</Aviso> : null}

          <Campo etiqueta="Email" value={email} readOnly disabled />
          {/* El foco arranca aca a proposito: la pantalla existe para pedir la contrasena. */}
          <Campo
            etiqueta="Contraseña"
            type="password"
            autoComplete="current-password"
            autoFocus
            value={contrasena}
            onChange={(e) => setContrasena(e.target.value)}
          />

          <div className="flex justify-between gap-3">
            <Boton variante="fantasma" onClick={() => void alSalir()}>
              Cerrar sesión
            </Boton>
            <Boton type="submit" disabled={trabajando || contrasena.length === 0}>
              {trabajando ? "Verificando..." : "Continuar"}
            </Boton>
          </div>
        </form>
      </div>
    </div>
  );
}
